import styled from '@emotion/styled';
import React, { useCallback, useState } from 'react';

import { DefaultTextInput } from '../forms/OCInputs';
import { connectLocation } from '../location/LocationConnectors';
import wrappedInReduxProvider from '../wrappedInReduxProvider';

const SearchInput = styled(DefaultTextInput)`
  padding: 16px 8px;
  border: 1px solid #585858;
  width: 100%;
`;

const AdvanceSearchTitle = styled.a`
  margin: 16px 0px;
  cursor: pointer;
  display: block;
  text-decoration: none;
`;

const AdvanceSpan = styled.span`
  font-size: 18px;
  margin: 0px 8px;
`;

const FiltersWrapper = styled.div`
  background: #f8f8f8;
  display: flex;
  padding: 16px;
  max-height: 416px;
  margin: 16px 0;
`;

const FilterGroupTitle = styled.span`
  font-weight: bold;
  font-size: 16px;
  color: #333333;
  padding-bottom: 8px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid black;
  padding: 0 40px;
  div {
    display: flex;
    flex-flow: column wrap;
    max-height: 340px;
  }
  :first-of-type {
    padding-left: 0;
  }
  :last-of-type {
    flex: 1;
    border-right: 0;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
`;

const Span = styled.span`
  padding-left: 8px;
  max-width: 139px;
  display: inline-flex;
`;

const SubmitButton = styled.button`
  min-width: 97px;
  height: 36px;
  margin-bottom: 64px;
`;

const filters = [
  {
    type: 'categories', // Required to extract data from metadata
    field: 'document_type', // Required to translate titles of Filter Groups; 'name' attribute of checkboxes
    i18n_key: 'sds.categories', // Required to translate filter labels
    options: [], // Required to save filter values and labels as objects
  },
  { type: 'regions', field: 'region', i18n_key: 'sds.regions', options: [] },
  {
    type: 'language_iso_codes',
    field: 'language',
    i18n_key: 'languages',
    options: [],
  },
];

const SdsSearch = connectLocation(({ t, locale, metadata, ...props }) => {
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const initialSelected = {};
  if (filters[0].options.length === 0) {
    filters.forEach(filter => {
      filter.title = t(`sds.fields.${filter.field}`);
      metadata[filter.type].forEach(val => {
        // Populating options with objects having translations as values, eg: {"en": "English"}
        filter.options.push({ [val]: t(`${filter.i18n_key}.${val}`) });
      });
      initialSelected[filter.type] = [];
    });
    // Sort the checkbox labels based on the translated values
    filters.forEach(filter => {
      filter.options.sort((a, b) =>
        Object.values(a)[0] > Object.values(b)[0] ? 1 : Object.values(b)[0] > Object.values(a)[0] ? -1 : 0
      );
    });
  }

  // initialSelected = {categories: [], regions: [], language_iso_codes: []}
  // It maintains the record of checked checkboxes in their respective groups.
  const [selected, setSelected] = useState(initialSelected);

  // toggleShowAdvancedSearch is a handler to Show/Hide Advanced Search Options
  const toggleShowAdvancedSearch = useCallback(() => {
    setShowAdvancedSearch(!showAdvancedSearch);
  }, [showAdvancedSearch]);

  // onCheckboxToggle handles the change of checkbox and updates `selected` state
  const onCheckboxToggle = useCallback(
    (category, value) => {
      let selectedCategory;
      const index = selected[category].indexOf(value);
      if (index === -1) {
        // Add value to options if not present initially
        selectedCategory = [...selected[category], value];
      } else {
        // Remove value from `selected` if already present
        selectedCategory = [...selected[category].slice(0, index), ...selected[category].slice(index + 1)];
      }
      setSelected({ ...selected, [category]: selectedCategory });
    },
    [selected]
  );

  // handleSearchChange maintains state of search field
  const handleSearchChange = useCallback(
    value => {
      setSearchValue(value);
    },
    [searchValue]
  );

  // getCheckedCount returns the total number of checkboxes from every filter group
  const getCheckedCount = () => {
    let count = 0;
    Object.values(selected).forEach(list => {
      count += list.length;
    });
    return count;
  };

  return (
    <div data-am-region="SDS Search">
      <form action="/sds/results" method="get" data-track="search-bar" data-track-search-term={searchValue}>
        <SearchInput
          id="sds-search-input"
          name="q"
          placeholder={t('sds.search_text')}
          onChange={handleSearchChange}
          value={searchValue}
        />
        <AdvanceSearchTitle
          onClick={toggleShowAdvancedSearch}
          data-track={showAdvancedSearch ? 'conceal' : 'reveal'}
          data-track-accordion="Advanced Search"
        >
          <i aria-hidden="true" className={`fa ${showAdvancedSearch ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
          <AdvanceSpan key={getCheckedCount()}>
            {t(`sds.advanced_search`)} ({getCheckedCount()})
          </AdvanceSpan>
        </AdvanceSearchTitle>
        {showAdvancedSearch ? (
          <SearchOptions selected={selected} onToggle={(category, value) => onCheckboxToggle(category, value)} />
        ) : null}
        {/* Hidden inputs for form submission with correct parameter format */}
        {Object.entries(selected).map(([category, values]) =>
          values.map(value => (
            <input
              key={`${category}-${value}`}
              type="hidden"
              name={`oc[${filters.find(f => f.type === category)?.field}][]`}
              value={value}
            />
          ))
        )}
        <SubmitButton type="submit" className="oc-cta-button">
          {t(`sds.submit`)}
        </SubmitButton>
      </form>
    </div>
  );
});

const SearchOptions = props => (
  <FiltersWrapper>
    {filters.map(filter => (
      <SearchFilterGroup key={filter.type} {...filter} {...props} />
    ))}
  </FiltersWrapper>
);

const SearchFilterGroup = props => {
  const { type, title, options, onToggle, selected } = props;

  const isChecked = (type, value) => selected[type].indexOf(value) > -1;

  return (
    <FilterGroup>
      <FilterGroupTitle>{title}</FilterGroupTitle>
      <div>
        {options.map(option => {
          // option format{"en": "English"}
          const filterCode = Object.keys(option)[0];
          const filterName = option[filterCode];
          return (
            <div key={filterCode} style={{ paddingTop: 8, paddingBottom: 8 }}>
              <label>
                <Checkbox
                  type="checkbox"
                  value={filterCode}
                  onChange={() => onToggle(type, filterCode)}
                  checked={isChecked(type, filterCode)}
                  data-track="filter"
                  data-track-filter-name={title}
                  data-track-filter-option={filterName}
                />
                <Span>{filterName}</Span>
              </label>
            </div>
          );
        })}
      </div>
    </FilterGroup>
  );
};

export default wrappedInReduxProvider(SdsSearch);
